// API service for Facebook Analytics
const API_BASE_URL = 'https://4ayc0kzlbd.execute-api.us-east-1.amazonaws.com/Prod';

class AnalyticsAPI {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async fetchWithTimeout(url, options = {}, timeout = 10000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  async getPages() {
    try {
      const response = await this.fetchWithTimeout(`${this.baseURL}/pages`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.pages || [];
    } catch (error) {
      console.error('Error fetching pages:', error);
      throw new Error('Failed to fetch pages');
    }
  }

  async getOverallAnalytics(days = 30) {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseURL}/analytics/overall?days=${days}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching overall analytics:', error);
      throw new Error('Failed to fetch overall analytics');
    }
  }

  async getPageAnalytics(pageId, days = 30) {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseURL}/analytics/page/${pageId}?days=${days}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching page analytics:', error);
      throw new Error('Failed to fetch page analytics');
    }
  }

  async getContentAnalytics(pageId, days = 30) {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseURL}/analytics/content/${pageId}?days=${days}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching content analytics:', error);
      throw new Error('Failed to fetch content analytics');
    }
  }

  async getAudienceAnalytics(pageId, days = 30) {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseURL}/analytics/audience/${pageId}?days=${days}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching audience analytics:', error);
      throw new Error('Failed to fetch audience analytics');
    }
  }

  async getTrendAnalytics(pageId, days = 30) {
    try {
      const response = await this.fetchWithTimeout(
        `${this.baseURL}/analytics/trends/${pageId}?days=${days}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching trend analytics:', error);
      throw new Error('Failed to fetch trend analytics');
    }
  }

  async getClientData(pageId = null, days = 30) {
    try {
      let url = `${this.baseURL}/data/client?days=${days}`;
      if (pageId) {
        url += `&page_id=${pageId}`;
      }
      
      const response = await this.fetchWithTimeout(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching client data:', error);
      throw new Error('Failed to fetch client data');
    }
  }

  async getHistoricalData(pageId = null, days = 30) {
    try {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const endDate = yesterday.toISOString().split('T')[0];
      
      let url = `${this.baseURL}/data/client?days=${days}&end_date=${endDate}`;
      if (pageId) {
        url += `&page_id=${pageId}`;
      }
      
      const response = await this.fetchWithTimeout(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching historical data:', error);
      throw new Error('Failed to fetch historical data');
    }
  }
}

// Create and export a singleton instance
const analyticsAPI = new AnalyticsAPI();
export default analyticsAPI; 